import { useEffect } from "react";
import i18n from "@/lib/i18n";
import { api } from "@/lib/api";

const STORAGE_KEY = "kaventine_language";
const SUPPORTED = ["en", "vi", "zh"];

/**
 * Suggests an initial language for a first-time visitor by asking the
 * backend to guess where the request is coming from (see
 * geo.service.ts's detectLanguageFromRequest - Vietnam -> vi, China -> zh,
 * everywhere else -> en). Only ever runs once, and only when nothing is
 * already stored: the moment a person has ANY language preference saved
 * (whether from this auto-detect itself on an earlier visit, or from
 * deliberately picking one in Settings), this hook is a no-op forever
 * after for that browser - an explicit or previously-resolved choice
 * always wins over a fresh guess, so switching networks/VPNs doesn't
 * silently change someone's language out from under them.
 *
 * Mount this once near the app's root (see App.tsx) - it has no UI of
 * its own.
 */
export function useLanguageAutoDetect() {
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return; // already resolved (auto or manual) - never override

    let cancelled = false;
    api
      .get<{ language: string }>("/api/geo/language")
      .then(({ language }) => {
        if (cancelled) return;
        if (SUPPORTED.includes(language) && language !== "en") {
          i18n.changeLanguage(language);
          // Best-effort, covers an already-logged-in user hitting this on
          // a fresh browser/device (empty localStorage there, but they do
          // have an account server-side already) - silently ignored if
          // there's no session yet, which is the common case (this runs
          // for anonymous, not-yet-registered visitors far more often).
          api.patch("/api/users/me", { language }).catch(() => {});
        }
        // language === "en" needs no explicit change (it's already the
        // fallback), but IS still worth marking resolved so this never
        // re-runs and re-fetches on every subsequent visit.
        localStorage.setItem(STORAGE_KEY, language);
      })
      .catch(() => {
        // Network hiccup, geo lookup unavailable, etc. - stay on the
        // default language for this visit; try again next time rather
        // than getting stuck in a broken state.
      });

    return () => {
      cancelled = true;
    };
  }, []);
}
